/** Shared session-expiry handling for every authenticated presentation capability. */

type AuthResponse = Pick<Response, 'status' | 'redirected' | 'url'>;

let relogining = false;

const AUTH_REASON_CODES = new Set([
  'ui_session_missing',
  'ui_session_expired',
  'session_or_key_missing',
  'session_exchange_failed',
  'identity_chain_incomplete',
  'identity_resolution_failed',
]);

/**
 * Přihlášen, ale bez oprávnění. NENÍ to autentizační stav — relogin by vedl na
 * login, ten by s platnou session poslal uživatele zpět a vznikla by smyčka
 * (Filip 2026-08-25). Patří sem hlášení, ne obnova session.
 */
const ACCESS_DENIED_REASON_CODES = new Set([
  'entity_without_access',
]);

export function isAuthenticationReasonCode(reasonCode?: string): boolean {
  return Boolean(reasonCode && AUTH_REASON_CODES.has(reasonCode));
}

export function isAccessDeniedReasonCode(reasonCode?: string): boolean {
  return Boolean(reasonCode && ACCESS_DENIED_REASON_CODES.has(reasonCode));
}

export function isAuthenticationResponse(response: AuthResponse): boolean {
  if (response.status === 401) return true;
  if (!response.url) return false;
  try {
    const path = new URL(response.url).pathname;
    return path === '/auth/login-page' || path === '/auth/oidc-login';
  } catch {
    return response.redirected && response.url.includes('/auth/');
  }
}

export function reloginUrl(
  location: Pick<Location, 'pathname' | 'search' | 'hash' | 'origin'>,
): string {
  const back = location.pathname + location.search + location.hash;
  const returnUrl = new URL(back, location.origin).toString();
  return `/auth/login-page?auto=1&return=${encodeURIComponent(returnUrl)}`;
}

/**
 * Zarážka proti zacyklení: jeden relogin za okno. Když se autentizační stav
 * vrátí hned po přihlášení, není to vypršelá session — je to stav, který se
 * přihlášením nespraví, a další redirect by jen roztočil smyčku.
 */
const RELOGIN_MARKER = 'acm.relogin.at';
const RELOGIN_COOLDOWN_MS = 30_000;

function reloginJustHappened(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOGIN_MARKER) ?? '0');
    return at > 0 && Date.now() - at < RELOGIN_COOLDOWN_MS;
  } catch {
    return false; // bez sessionStorage se chováme jako dřív
  }
}

function markRelogin(): void {
  try {
    sessionStorage.setItem(RELOGIN_MARKER, String(Date.now()));
  } catch { /* private mode */ }
}

/**
 * Když autentizační stav přijde HNED PO přihlášení, přihlášení ho nespraví:
 * session je platná, ale server ji nedokáže vyměnit za entity-bound credential.
 * Dashboard pak NESMÍ zůstat zobrazený s tichým selháváním (Filip 2026-09-05,
 * CIEACM000: „když identitu nelze ověřit, nemám se nikam dostat") — místo
 * dalšího redirectu na login jde na serverovou blokující stránku.
 */
export function blockedUrl(reasonCode: string): string {
  return `/auth/access-denied?reason=${encodeURIComponent(reasonCode)}`;
}

/** @returns true když redirect (na login, nebo na blokující stránku) opravdu proběhl. */
export function triggerRelogin(reasonCode = 'ui_session_expired'): boolean {
  if (typeof window === 'undefined' || relogining) return false;
  relogining = true;
  if (reloginJustHappened()) {
    window.location.replace(blockedUrl(reasonCode));
    return true;
  }
  markRelogin();
  window.location.href = reloginUrl(window.location);
  return true;
}

export function triggerReloginForResponse(response: AuthResponse): boolean {
  if (!isAuthenticationResponse(response)) return false;
  return triggerRelogin(response.status === 401 ? 'ui_session_missing' : 'ui_session_expired');
}
